import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import GroomingBookingForm from '../components/GroomingBookingForm';
import GroomingBookingsDisplay from '../components/GroomingBookingsDisplay';
import GroomingBookingCard from '../components/GroomingBookingCard';
import useGroomingBookings from '../hooks/useGroomingBookings';
import '../styles/pages/grooming.css';

const Grooming = () => {
    const {
        bookings,
        todayBookings,
        upcomingBookings,
        loading,
        error,
        createBooking,
        updateBooking,
        deleteBooking,
        clearError
    } = useGroomingBookings();

    const [activeTab, setActiveTab] = useState('overview');
    const [editingBooking, setEditingBooking] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [grCollapsedPast, setGrCollapsedPast] = useState(true);

    const location = useLocation();

    // Open booking form tab if navigated via FAB with state
    useEffect(() => {
        if (location.state?.openTab === 'booking-form') {
            setActiveTab('booking-form');
            setEditingBooking(null);
        }
    }, [location.state]);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    // Handle form submission (create or update)
    const handleFormSubmit = async (formData) => {
        try {
            clearError();

            if (editingBooking) {
                await updateBooking(editingBooking._id, formData);
                setMessage({ type: 'success', text: '¡Cita de estética actualizada con éxito!' });
            } else {
                await createBooking(formData);
                setMessage({ type: 'success', text: '¡Cita de estética programada con éxito!' });
            }
            
            setEditingBooking(null);
            setActiveTab('overview');
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // Handle edit booking
    const handleEdit = (booking) => {
        setEditingBooking(booking);
        setActiveTab('booking-form');
    };

    // Handle status update
    const handleStatusUpdate = async (bookingId, newStatus) => {
        try {
            clearError();
            await updateBooking(bookingId, { status: newStatus });
            setMessage({ type: 'success', text: `¡Estado de la reserva actualizado a ${newStatus}!` });
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // Handle payment status update
    const handlePaymentUpdate = async (bookingId, newPaymentStatus) => {
        try {
            clearError();
            await updateBooking(bookingId, { paymentStatus: newPaymentStatus });
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // Handle delete booking
    const handleDelete = async (bookingId) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta cita de estética?')) {
            try {
                clearError();
                await deleteBooking(bookingId);
                setMessage({ type: 'success', text: '¡Cita de estética eliminada con éxito!' });
            } catch (err) {
                setMessage({ type: 'error', text: err.message });
            }
        }
    };

    // Handle form cancel
    const handleFormCancel = () => {
        setEditingBooking(null);
        setActiveTab('overview');
    };

    // Filter bookings for the all bookings tab
    const filteredBookings = bookings.filter(booking => {
        const matchesSearch = !searchTerm || 
            booking.dog?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.dog?.owners?.some(owner => 
                owner.name.toLowerCase().includes(searchTerm.toLowerCase())
            ) ||
            booking.ownerPhone.includes(searchTerm);
        
        const matchesStatus = !statusFilter || booking.status === statusFilter;
        
        return matchesSearch && matchesStatus;
    });

    return (
        <div>
            <Navigation />
            <div className="grooming-container">
                <div className="grooming-header">
                    <h1>✂️ Servicios de Estética</h1>
                    <p className="page-subtitle">Administra citas y servicios de estética</p>
                </div>

                {/* Messages */}
                {(message.text || error) && (
                    <div className={`message ${message.type || 'error'}`}>
                        {message.text || error}
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="gr-tab-nav">
                    <button
                        onClick={() => {
                            setActiveTab('overview');
                            setEditingBooking(null);
                        }}
                        className={`gr-tab-btn${activeTab === 'overview' ? ' active' : ''}`}
                    >
                        📋 Resumen
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('booking-form');
                            if (!editingBooking) setEditingBooking(null);
                        }}
                        className={`gr-tab-btn${activeTab === 'booking-form' ? ' active' : ''}`}
                    >
                        {editingBooking ? '✏️ Editar cita' : '➕ Nueva cita'}
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('all-bookings');
                            setEditingBooking(null);
                        }}
                        className={`gr-tab-btn${activeTab === 'all-bookings' ? ' active' : ''}`}
                    >
                        📅 Todas las citas
                    </button>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="overview-tab">
                        <div className="quick-stats">
                            <div className="stat-card">
                                <h3>🗓️ Hoy</h3>
                                <p className="stat-number">{todayBookings.length}</p>
                                <p className="stat-label">cita{todayBookings.length !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="stat-card">
                                <h3>📅 Próximas</h3>
                                <p className="stat-number">{upcomingBookings.length}</p>
                                <p className="stat-label">cita{upcomingBookings.length !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="stat-card">
                                <h3>📊 Total</h3>
                                <p className="stat-number">{bookings.length}</p>
                                <p className="stat-label">reserva{bookings.length !== 1 ? 's' : ''}</p>
                            </div>
                        </div>

                        <GroomingBookingsDisplay
                            todayBookings={todayBookings}
                            upcomingBookings={upcomingBookings}
                            loading={loading}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onStatusUpdate={handleStatusUpdate}
                            onPaymentUpdate={handlePaymentUpdate}
                        />
                    </div>
                )}

                {/* Booking Form Tab */}
                {activeTab === 'booking-form' && (
                    <div className="booking-form-tab">
                        <GroomingBookingForm
                            onSubmit={handleFormSubmit}
                            onCancel={handleFormCancel}
                            loading={loading}
                            editingBooking={editingBooking}
                        />
                    </div>
                )}

                {/* All Bookings Tab */}
                {activeTab === 'all-bookings' && (
                    <div className="all-bookings-tab">
                        <div className="bookings-header">
                            <h2>Todas las citas de estética</h2>

                            {/* Filters */}
                            <div className="bookings-filters">
                                <input
                                    type="text"
                                    className="gr-search-input"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Buscar por perro, propietario o teléfono…"
                                />
                                <div className="gr-status-chips">
                                    {[
                                        { value: '',           label: 'Todos'      },
                                        { value: 'scheduled',  label: 'Programada' },
                                        { value: 'in-progress',label: 'En curso'   },
                                        { value: 'completed',  label: 'Completada' },
                                        { value: 'cancelled',  label: 'Cancelada'  },
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            className={`gr-status-chip${statusFilter === opt.value ? ' active' : ''}`}
                                            onClick={() => setStatusFilter(opt.value)}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading">Cargando todas las citas de estética...</div>
                        ) : filteredBookings.length === 0 ? (
                            <div className="no-bookings">
                                {searchTerm || statusFilter ? (
                                    <p>Ninguna cita coincide con tu búsqueda.</p>
                                ) : (
                                    <p>No se encontraron citas de estética. ¡Crea la primera!</p>
                                )}
                            </div>
                        ) : (() => {
                            const now = new Date();
                            const todayY = now.getFullYear(), todayM = now.getMonth(), todayD = now.getDate();
                            const dow = now.getDay();
                            const mOff = dow === 0 ? -6 : 1 - dow;
                            const weekStart = new Date(todayY, todayM, todayD + mOff);
                            const weekEnd = new Date(todayY, todayM, todayD + mOff + 7);
                            const monthStart = new Date(todayY, todayM, 1);
                            const nextMonthStart = new Date(todayY, todayM + 1, 1);
                            const afterNextMonth = new Date(todayY, todayM + 2, 1);
                            const sortAsc = arr => [...arr].sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate));
                            const sortDesc = arr => [...arr].sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate));

                            const grSections = [
                                { key: 'thisweek',  label: 'Esta semana',   items: sortAsc(filteredBookings.filter(b => { const d = new Date(b.appointmentDate); return d >= weekStart && d < weekEnd; })) },
                                { key: 'thismonth', label: 'Este mes',      items: sortAsc(filteredBookings.filter(b => { const d = new Date(b.appointmentDate); return d >= monthStart && d < nextMonthStart && !(d >= weekStart && d < weekEnd); })) },
                                { key: 'nextmonth', label: 'Mes siguiente', items: sortAsc(filteredBookings.filter(b => { const d = new Date(b.appointmentDate); return d >= nextMonthStart && d < afterNextMonth; })) },
                                { key: 'later',     label: 'Más adelante', items: sortAsc(filteredBookings.filter(b => new Date(b.appointmentDate) >= afterNextMonth)) },
                                { key: 'earlier',   label: 'Anteriores',   items: sortDesc(filteredBookings.filter(b => new Date(b.appointmentDate) < monthStart)) },
                            ].filter(s => s.items.length > 0);

                            return grSections.map(({ key, label, items }) => {
                                const isCollapsed = key === 'earlier' && grCollapsedPast;
                                return (
                                    <div key={key} className="gr-time-section">
                                        <div className="gr-section-row">
                                            <span className="gr-section-label">{label} · {items.length}</span>
                                            {key === 'earlier' && (
                                                <button
                                                    className="gr-section-toggle"
                                                    onClick={() => setGrCollapsedPast(v => !v)}
                                                >
                                                    {isCollapsed ? 'Mostrar' : 'Ocultar'}
                                                </button>
                                            )}
                                        </div>
                                        {!isCollapsed && (
                                            <div className="bookings-grid">
                                                {items.map(booking => (
                                                    <GroomingBookingCard
                                                        key={booking._id}
                                                        booking={booking}
                                                        onEdit={handleEdit}
                                                        onDelete={handleDelete}
                                                        onStatusUpdate={handleStatusUpdate}
                                                        onPaymentUpdate={handlePaymentUpdate}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            });
                        })()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Grooming;